import { useEffect, useState } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { FolderKanban, Search, CalendarPlus } from 'lucide-react';
import FilterBar from '@/components/FilterBar';
import AIAnalysisPanel from '@/components/AIAnalysisPanel';
import IssueIdLink from '@/components/IssueIdLink';
import AddToCalendarModal from '@/components/AddToCalendarModal';
import CalendarIndicator from '@/components/CalendarIndicator';
import useCalendarizedIssues from '@/hooks/useCalendarizedIssues';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ProjectView({ token }) {
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [filters, setFilters] = useState({ project: '', category: '', user: '', status: '', includeResolved: false });
  const [calendarModalIssue, setCalendarModalIssue] = useState(null);
  
  // Calendar tracking
  const { getCalendarEvents, refresh: refreshCalendar } = useCalendarizedIssues(token);

  const COLORS = {
    primary: 'rgb(59, 130, 246)',
    accent: 'rgb(16, 185, 129)',
    warning: 'rgb(245, 158, 11)',
    destructive: 'rgb(239, 68, 68)',
    secondary: 'rgb(161, 161, 170)',
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  useEffect(() => {
    applyFilters();
  }, [filters, issues]);

  const fetchData = async () => {
    try {
      const analyticsParams = new URLSearchParams();
      if (filters.includeResolved) {
        analyticsParams.append('include_resolved', 'true');
      }
      
      const analyticsResponse = await axios.get(`${BACKEND_URL}/api/analytics/overview?${analyticsParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      });
      setAnalytics(analyticsResponse.data);
      
      await fetchFilteredIssues();
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredIssues = async () => {
    try {
      const params = new URLSearchParams({ page_size: 1000 });
      
      // Add includeResolved flag (default is false = stato < 80)
      if (filters.includeResolved) {
        params.append('include_resolved', 'true');
      }
      
      if (filters.status && filters.status !== 'all') {
        params.append('filter_state', filters.status);
      }
      if (filters.project && filters.project !== 'all') {
        params.append('filter_project', filters.project);
      }
      if (filters.category && filters.category !== 'all') {
        params.append('filter_category', filters.category);
      }
      if (filters.user && filters.user !== 'all') {
        params.append('filter_user', filters.user);
      }

      const issuesResponse = await axios.get(`${BACKEND_URL}/api/issues?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      });
      
      setIssues(issuesResponse.data.issues);
      setFilteredIssues(issuesResponse.data.issues);
    } catch (error) {
      toast.error('Errore nel caricamento issues');
      console.error(error);
    }
  };

  const applyFilters = () => {
    let filtered = [...issues];
    
    if (filters.status) {
      filtered = filtered.filter(issue => issue.status?.name === filters.status);
    }
    if (filters.project) {
      filtered = filtered.filter(issue => issue.project?.name === filters.project);
    }
    if (filters.category) {
      filtered = filtered.filter(issue => issue.category?.name === filters.category);
    }
    if (filters.user) {
      filtered = filtered.filter(issue => {
        const handler = issue.handler?.real_name || issue.handler?.name;
        return handler === filters.user;
      });
    }
    
    setFilteredIssues(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const analyticsParams = new URLSearchParams();
      if (filters.includeResolved) {
        analyticsParams.append('include_resolved', 'true');
      }
      
      const analyticsResponse = await axios.get(`${BACKEND_URL}/api/analytics/overview?${analyticsParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      });
      setAnalytics(analyticsResponse.data);
      
      await fetchFilteredIssues();
      toast.success('Dati aggiornati con successo!');
    } catch (error) {
      toast.error(`Errore nell'aggiornamento: ${error.message}`);
    } finally {
      setRefreshing(false);
    }
  };



  const projectData = Object.entries(analytics?.by_project || {})
    .map(([name, count]) => ({ name, issues: count }))
    .sort((a, b) => b.issues - a.issues);

  const filteredProjects = projectData.filter((project) =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getProjectIssues = (projectName) => {
    return issues.filter((issue) => issue.project?.name === projectName);
  };

  const getProjectStats = (projectName) => {
    const projectIssues = getProjectIssues(projectName);
    const statusCounts = {};
    
    projectIssues.forEach((issue) => {
      const status = issue.status?.name || 'Unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    return {
      total: projectIssues.length,
      byStatus: Object.entries(statusCounts).map(([name, value]) => ({ name, value })),
    };
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('resolved') || s.includes('closed')) return 'rgb(16, 185, 129)';
    if (s.includes('assigned') || s.includes('in progress')) return 'rgb(59, 130, 246)';
    if (s.includes('feedback') || s.includes('acknowledged')) return 'rgb(245, 158, 11)';
    return 'rgb(161, 161, 170)';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading-spinner">
        <div className="text-[rgb(161,161,170)] font-mono">Caricamento...</div>
      </div>
    );
  }

  const projects = [...new Set((issues || []).map(i => i.project?.name).filter(Boolean))];
  const categories = [...new Set((issues || []).map(i => i.category?.name).filter(Boolean))];
  const users = [...new Set((issues || []).map(i => i.handler?.real_name || i.handler?.name).filter(Boolean))];
  const statuses = [...new Set((issues || []).map(i => i.status?.name).filter(Boolean))];

  return (
    <div className="space-y-6" data-testid="project-view">
      <div className="mb-8">
        <h1 className="text-4xl font-bold font-mono mb-2" data-testid="project-view-title">
          Vista Progetti
        </h1>
        <p className="text-[rgb(161,161,170)] text-sm font-mono uppercase tracking-widest">
          Project overview & drill-down
        </p>
      </div>

      <FilterBar
        projects={projects}
        categories={categories}
        users={users}
        statuses={statuses}
        onFilterChange={handleFilterChange}
        onRefresh={handleRefresh}
        isRefreshing={refreshing}
        onAIAnalysis={() => setAiPanelOpen(true)}
      />

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[rgb(161,161,170)]" />
        <Input
          type="text"
          placeholder="Cerca progetto..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-black/20 border-[rgb(39,39,42)] focus:border-[rgb(59,130,246)] focus:ring-1 focus:ring-[rgb(59,130,246)] font-mono"
          data-testid="project-search-input"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Distribution Chart */}
        <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm" data-testid="project-distribution-chart">
          <div className="border-b border-[rgb(39,39,42)]/50 pb-4 mb-4">
            <h3 className="text-xl font-mono font-bold">Distribuzione Issues per Progetto</h3>
            <p className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono mt-1">
              Top projects
            </p>
          </div>
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={filteredProjects.slice(0, 8)}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="issues"
              >
                {filteredProjects.slice(0, 8).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index % Object.values(COLORS).length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgb(24, 24, 27)',
                  border: '1px solid rgb(39, 39, 42)',
                  borderRadius: '0.375rem',
                  fontFamily: 'JetBrains Mono, monospace',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Project List */}
        <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm" data-testid="project-list">
          <div className="border-b border-[rgb(39,39,42)]/50 pb-4 mb-4">
            <h3 className="text-xl font-mono font-bold">Elenco Progetti</h3>
            <p className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono mt-1">
              Click per drill-down
            </p>
          </div>
          <div className="space-y-2 overflow-y-auto" style={{ maxHeight: '350px' }}>
            {filteredProjects.map((project) => (
              <div
                key={project.name}
                onClick={() => setSelectedProject(project.name)}
                className={`
                  p-3 rounded-sm cursor-pointer border transition-all duration-200
                  ${
                    selectedProject === project.name
                      ? 'bg-[rgb(59,130,246)]/10 border-[rgb(59,130,246)]/50'
                      : 'bg-[rgb(39,39,42)]/30 border-[rgb(39,39,42)] hover:border-[rgb(59,130,246)]/50 hover:bg-[rgb(39,39,42)]/50'
                  }
                `}
                data-testid={`project-item-${project.name}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FolderKanban className="h-4 w-4" />
                    <span className="font-mono text-sm">{project.name}</span>
                  </div>
                  <Badge className="rounded-sm px-2 py-0.5 text-xs font-mono bg-[rgb(59,130,246)]">
                    {project.issues}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Project Detail - Drill Down */}
      {selectedProject && (() => {
        const stats = getProjectStats(selectedProject);
        return (
          <div className="space-y-6" data-testid="project-detail">
            {/* Project Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm">
                <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono block mb-2">
                  Progetto Selezionato
                </span>
                <div className="text-2xl font-bold font-mono">{selectedProject}</div>
              </div>
              <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm">
                <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono block mb-2">
                  Total Issues
                </span>
                <div className="text-4xl font-bold font-mono text-[rgb(59,130,246)]">{stats.total}</div>
              </div>
              <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm">
                <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono block mb-2">
                  Stati Diversi
                </span>
                <div className="text-4xl font-bold font-mono text-[rgb(16,185,129)]">{stats.byStatus.length}</div>
              </div>
            </div>

            {/* Status Distribution for Selected Project */}
            <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm">
              <div className="border-b border-[rgb(39,39,42)]/50 pb-4 mb-4">
                <h3 className="text-xl font-mono font-bold">Distribuzione per Status</h3>
                <p className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono mt-1">
                  {selectedProject}
                </p>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.byStatus}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgb(39, 39, 42)" />
                  <XAxis
                    dataKey="name"
                    stroke="rgb(161, 161, 170)"
                    style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
                  />
                  <YAxis stroke="rgb(161, 161, 170)" style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgb(24, 24, 27)',
                      border: '1px solid rgb(39, 39, 42)',
                      borderRadius: '0.375rem',
                      fontFamily: 'JetBrains Mono, monospace',
                    }}
                  />
                  <Bar dataKey="value" fill={COLORS.primary} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Project Issues Table */}
            <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm">
              <div className="border-b border-[rgb(39,39,42)]/50 pb-4 mb-4">
                <h3 className="text-xl font-mono font-bold">Issues del Progetto</h3>
                <p className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono mt-1">
                  {getProjectIssues(selectedProject).length} issues totali
                </p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-[rgb(39,39,42)]">
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">ID</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Summary</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Status</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Priority</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Assignee</TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)] w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getProjectIssues(selectedProject).map((issue) => (
                      <TableRow key={issue.id} className="border-[rgb(39,39,42)] hover:bg-white/5" data-testid={`issue-row-${issue.id}`}>
                        <TableCell className="font-mono text-sm">
                          <div className="flex items-center gap-1.5">
                            <IssueIdLink issueId={issue.id} />
                            <CalendarIndicator events={getCalendarEvents(issue.id)} />
                          </div>
                        </TableCell>
                        <TableCell className="max-w-md truncate text-sm">{issue.summary}</TableCell>
                        <TableCell>
                          <Badge
                            className="rounded-sm px-2 py-0.5 text-xs font-mono uppercase tracking-wider"
                            style={{ backgroundColor: getStatusColor(issue.status?.name) }}
                          >
                            {issue.status?.name || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{issue.priority?.label || 'N/A'}</TableCell>
                        <TableCell className="text-sm">{issue.handler?.real_name || issue.handler?.name || 'Unassigned'}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => setCalendarModalIssue(issue)}
                            className="p-1.5 rounded hover:bg-[rgb(63,63,70)] text-[rgb(161,161,170)] hover:text-[rgb(59,130,246)] transition-colors"
                            title="Aggiungi al calendario"
                          >
                            <CalendarPlus className="h-4 w-4" />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Calendar Modal */}
      {calendarModalIssue && (
        <AddToCalendarModal
          issue={calendarModalIssue}
          token={token}
          onClose={() => setCalendarModalIssue(null)}
          onSuccess={() => {
            toast.success('Evento aggiunto al calendario!');
            refreshCalendar();
          }}
        />
      )}

      <AIAnalysisPanel
        isOpen={aiPanelOpen}
        onClose={() => setAiPanelOpen(false)}
        issues={filteredIssues}
        token={token}
      />
      <AIAnalysisPanel
        issues={filteredIssues}
        context="Vista Progetti - Analisi distribuzione issue per progetto e performance"
        token={token}
        isOpen={aiPanelOpen}
        onClose={() => setAiPanelOpen(false)}
      />
    </div>
  );
}

export default ProjectView;